import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

export interface Candidate {
  email: string;
  name: string;
  source: string;
  score: number;
}

interface Props {
  /** Adressen uit de huidige mail. Leeg bij doorsturen — dan telt alleen het adresboek. */
  candidates?: Candidate[];
  value: string;
  onChange: (v: string) => void;
  /** Optional: render een CC/BCC link rechts in dezelfde rij. */
  onShowCcBcc?: () => void;
  showCcBccButton?: boolean;
  /**
   * 'inline' (default) = compacte composer-rij met eigen "Aan"-label links.
   * 'stacked' = het label staat al boven het veld (dialoog); de picker rendert
   * alleen het invoerveld, in de grotere dialoog-veldstijl.
   */
  variant?: 'inline' | 'stacked';
  /**
   * Lijstveld: meerdere ontvangers gescheiden door komma of puntkomma. Zoeken
   * gebeurt dan op het laatste segment en een keuze vervangt alléén dat
   * segment, zodat eerder ingevulde adressen blijven staan.
   */
  multiple?: boolean;
  disabled?: boolean;
}

/** Splitst een lijstveld in [alles daarvoor, laatste segment]. */
function splitsLaatsteSegment(value: string): [string, string] {
  const idx = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'));
  if (idx === -1) return ['', value];
  return [value.slice(0, idx + 1), value.slice(idx + 1)];
}

/** Gehoist zodat de default-prop geen nieuwe array-identiteit per render geeft. */
const GEEN_KANDIDATEN: Candidate[] = [];

const SOURCE_LABEL: Record<string, string> = {
  'lastIncoming.reply_to': 'Reply-To (echte afzender)',
  'lastIncoming.from': 'Afzender laatste mail',
  'conversation.from': 'Oorspronkelijke afzender',
  'reply-to-quote': 'Genoemd in quoted mail',
  'from-quote': 'From: in quoted mail',
  'inline': 'Genoemd in body',
  'contact': 'Klant',
  'inbox': 'Eerder gemaild',
  'partner': 'Partner',
  'reparateur': 'Reparateur',
};

/**
 * Smart "Aan"-veld combobox. Bij focus toont-ie de adressen uit de huidige
 * mail (thread-kandidaten). Zodra je typt (>=2 tekens) zoekt-ie live in het
 * adresboek (/api/inbox/address-search: inbox-correspondenten + klanten) zodat
 * je een bekend adres kunt kiezen i.p.v. overtypen — minder bounces door typo's.
 *
 * Volgt project React-conventies: click-outside via document listener met
 * cleanup, debounced fetch met AbortController, geen index-based keys.
 */
export function AanFieldPicker({
  candidates = GEEN_KANDIDATEN,
  value,
  onChange,
  onShowCcBcc,
  showCcBccButton,
  variant = 'inline',
  multiple = false,
  disabled = false,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [directory, setDirectory] = useState<Candidate[]>([]);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Onderdruk de eerstvolgende adresboek-zoekopdracht direct na een selectie,
  // zodat het aanklikken van een adres de dropdown niet meteen heropent.
  const justSelectedRef = useRef(false);

  // Click-outside + Escape sluiten de dropdown.
  useEffect(() => {
    if (!isOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isOpen]);

  // Bij een lijstveld telt alleen het segment waar de cursor in staat; anders
  // het hele veld.
  const [prefix, actiefSegment] = multiple ? splitsLaatsteSegment(value) : ['', value];

  // Debounced adresboek-zoekopdracht op de getypte waarde.
  const query = actiefSegment.trim();
  useEffect(() => {
    if (!isOpen) return;
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    if (query.length < 2) {
      setDirectory([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const { supabase } = await import('../../lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const res = await fetch(
          `/api/inbox/address-search?q=${encodeURIComponent(query)}`,
          { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal },
        );
        if (!res.ok) return;
        const data: { matches?: Candidate[] } = await res.json();
        setDirectory(data.matches || []);
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('[AanFieldPicker] address-search error:', err);
        }
      }
    }, 200);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, isOpen]);

  // Gecombineerde, gededupliceerde lijst: thread-kandidaten eerst (hoogste
  // signaal voor "beantwoord deze persoon"), daarna adresboek-resultaten. Het
  // exact al ingevulde adres verbergen we — dat toevoegen zou alleen ruis zijn.
  const options = useMemo(() => {
    const current = actiefSegment.trim().toLowerCase();
    const q = current;
    const seen = new Set<string>();
    const out: Candidate[] = [];
    const threadFiltered = q.length < 2
      ? candidates
      : candidates.filter(
          (c) =>
            c.email.toLowerCase().includes(q) ||
            (c.name || '').toLowerCase().includes(q),
        );
    for (const c of [...threadFiltered, ...directory]) {
      const key = c.email.toLowerCase();
      if (key === current || seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  }, [candidates, directory, actiefSegment]);

  useEffect(() => {
    setHighlight(-1);
  }, [options]);

  const select = (email: string) => {
    justSelectedRef.current = true;
    // In een lijstveld vervangen we alleen het segment waar je in typte; de
    // eerder gekozen ontvangers blijven staan.
    onChange(multiple && prefix ? `${prefix} ${email}` : email);
    setIsOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setIsOpen(true);
      return;
    }
    if (!options.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? options.length - 1 : h - 1));
    } else if (e.key === 'Enter' && highlight >= 0) {
      const chosen = options[highlight];
      if (chosen) {
        e.preventDefault();
        select(chosen.email);
      }
    }
  };

  const showDropdown = isOpen && options.length > 0;

  return (
    <div ref={containerRef} className="flex items-center gap-2 relative">
      {variant === 'inline' && <label className="text-xs text-slate-500 w-8">Aan</label>}
      <div className="flex-1 relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Naam of e-mailadres..."
          autoComplete="off"
          disabled={disabled}
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          className={clsx(
            'w-full text-sm text-slate-700 pr-8',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent',
            variant === 'stacked'
              ? 'border border-slate-300 rounded-lg px-3 py-2'
              : 'border border-slate-200 rounded-md px-3 py-1.5',
          )}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setIsOpen((o) => !o);
            inputRef.current?.focus();
          }}
          aria-label="Toon bekende adressen"
          aria-expanded={showDropdown}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </button>
        {showDropdown && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 bg-slate-50 border-b border-slate-100">
              {query.length < 2 ? 'Adressen uit deze mail' : 'Bekende adressen'}
            </div>
            <ul className="max-h-64 overflow-y-auto py-1">
              {options.map((c, i) => (
                <li key={c.email}>
                  <button
                    type="button"
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => select(c.email)}
                    className={clsx(
                      'w-full text-left px-3 py-2 focus:outline-none',
                      i === highlight ? 'bg-indigo-50' : 'hover:bg-indigo-50',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-sm text-slate-900 truncate">
                        {c.name ? `${c.name} <${c.email}>` : c.email}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {SOURCE_LABEL[c.source] || c.source}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {showCcBccButton && onShowCcBcc && (
        <button
          type="button"
          onClick={onShowCcBcc}
          className="text-xs text-slate-400 hover:text-slate-600 whitespace-nowrap"
        >
          CC / BCC
        </button>
      )}
    </div>
  );
}
